import {
  ChangeEvent,
  MouseEvent,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import Addresses from './Addresses.tsx';
import Contacts from './Contacts.tsx';
import AccountNumbers from './AccountNumbers.tsx';
import {
  Customer,
  CustomerAdmin,
  CustomerLocation,
  TradingPartner,
  TradingPartnerAccountNumber,
  TradingPartnerAddress,
  TradingPartnerContact,
  TradingPartnerGlobalLocationNumber,
} from '../../../common/types/models.ts';
import { TradingPartnerType } from '../../../common/enums.ts';
import GlobalLocationNumbers from './GlobalLocationNumbers.tsx';
import api from '../../../common/api/index.ts';
import { useNavigate } from 'react-router-dom';
import { toast } from '../../../func.ts';
import queryHooks from '../../../common/queryHooks/index.ts';
import useCurrentCustomerLocationId from '../../../hooks/useCurrentCustomerLocation.ts';

const General = () => {
  const navigate = useNavigate();
  const { customerLocationId } = useCurrentCustomerLocationId();
  const initValues = (): Partial<TradingPartner> => ({
    name: '',
    type: TradingPartnerType.upstream,
  });

  const [selectedTradingPartnerId, setSelectedTradingPartnerId] =
    useState<string>('');
  const [values, setValues] = useState<Partial<TradingPartner>>(initValues());
  const [globalLocationNumbers, setGlobalLocationNumbers] = useState<
    Partial<TradingPartnerGlobalLocationNumber>[]
  >([]);
  const [addresses, setAddresses] = useState<Partial<TradingPartnerAddress>[]>(
    []
  );
  const [contacts, setContacts] = useState<Partial<TradingPartnerContact>[]>(
    []
  );
  const [accountNumbers, setAccountNumbers] = useState<
    Partial<TradingPartnerAccountNumber>[]
  >([]);
  const [customerLocationList, setCustomerLocationList] = useState<
    CustomerLocation[]
  >([]);
  const [admin, setAdmin] = useState<CustomerAdmin | null>(null);

  const { data: tradingPartnerList } =
    queryHooks.tradingPartner.useReadAllByExcludedCustomerLocationId(
      customerLocationId
    );

  const onChange = ({
    target: { id, value },
  }: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setValues(prev => ({ ...prev, [id]: value }));

  const createTradingPartner = async (event: MouseEvent<HTMLAnchorElement>) => {
    event?.preventDefault();
    const { name } = values;

    if (!name)
      return toast({
        payload: {
          msg: 'Trading Partner Name is required',
          class: 'red white-text',
        },
      });

    if (!contacts?.length)
      return toast({
        payload: {
          msg: 'You must enter at least one contact',
          class: 'red white-text',
        },
      });

    if (
      !contacts?.every(
        contact => contact.contactDetails && contact.contactDetails?.length > 0
      )
    )
      return toast({
        payload: {
          msg: 'One or more entered contacts are missing required details',
          class: 'red white-text',
        },
      });

    if (!accountNumbers?.length)
      return toast({
        payload: {
          msg: 'You must enter at least one account number',
          class: 'red white-text',
        },
      });

    const payload = {
      customerLocationId: customerLocationId,
      name: values?.name?.trim(),
      type: values.type,
      globalLocationNumbers,
      addresses,
      contacts,
      accountNumbers,
    };

    const response = await api.tradingPartner.create.one(
      payload as any as TradingPartner
    );
    if (response) navigate('/tradingpartner');
  };

  const getCustomerLocations = async () => {
    const customerLocations: CustomerLocation[] =
      (await api.customerLocation.read.all.by.userId()) ?? [];
    if (customerLocations.length > 0) {
      setCustomerLocationList([...customerLocations]);
    } else {
      const customer: Customer | null = await api.customer.read.one.by.userId();
      const admin: CustomerAdmin | null =
        await api.customerLocation.read.one.admin.by.customerId(
          customer?.id ?? 0
        );
      setAdmin(admin);
    }
  };

  useEffect(() => {
    setSelectedTradingPartnerId('');
  }, [customerLocationId]);

  useEffect(() => {
    getCustomerLocations();
    M.FormSelect.init(document.querySelectorAll('#type'));

    return () => {
      const el = document.querySelector('#type');
      if (el) M.FormSelect.getInstance(el)?.destroy();
    };
  }, []);

  useEffect(() => {
    if (parseInt(selectedTradingPartnerId) === 0) {
      setAddresses([]);
      setContacts([]);
    }
    const tradingPartner = tradingPartnerList?.find(
      tp => tp.id === parseInt(selectedTradingPartnerId)
    );
    setAddresses(tradingPartner?.addresses ? tradingPartner.addresses : []);
    setContacts(tradingPartner?.contacts ? tradingPartner.contacts : []);
    setValues(prev => ({
      ...prev,
      name: tradingPartner?.name,
    }));
  }, [selectedTradingPartnerId]);

  useLayoutEffect(() => {
    const selects = document.querySelectorAll('.tpGenerealSelect');

    selects.forEach(select => {
      M.FormSelect.getInstance(select)?.destroy();
    });

    M.FormSelect.init(selects);

    return () => {
      selects.forEach(select => {
        M.FormSelect.getInstance(select)?.destroy();
      });
    };
  }, [tradingPartnerList]);

  return (
    <div>
      {customerLocationList.length === 0 ? (
        <div style={{ color: 'red', margin: '10px', fontSize: '20px' }}>
          {`* Your admin has not given you access to any of your business locations. Please contact your admin, ${admin?.name}, at ${admin?.email}.`}
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            paddingTop: '20px',
          }}
        >
          <p style={{ paddingLeft: '20px', fontSize: '20px' }}>
            {customerLocationId
              ? customerLocationList.find(
                  l => String(l.id) === String(customerLocationId)
                )?.name
              : customerLocationList[0].name}
          </p>
        </div>
      )}
      <div className="row">
        <h5>New Trading Partner</h5>
        <div className="divider" />
      </div>
      {customerLocationList?.length > 1 ? (
        <div className="row">
          <div className="input-field col s12 m12">
            <select
              id="tradingPartnerId"
              className="tpGenerealSelect"
              value={selectedTradingPartnerId}
              onChange={e => setSelectedTradingPartnerId(e.target.value)}
            >
              <option value="" disabled>
                Choose trading partner
              </option>
              <option key="none" value="0">
                None
              </option>
              {tradingPartnerList
                ?.filter(tp => tp.isActive)
                .map(({ id, name, customerLocation }) => {
                  return (
                    <option key={id} value={String(id)}>
                      {name} - {`(${customerLocation.name})`}
                    </option>
                  );
                })}
            </select>
            <label htmlFor="tradingPartnerId">
              Copy Existing Trading Partner
            </label>
          </div>
        </div>
      ) : null}
      <div className="row">
        <div className="input-field col s12 m6">
          <input
            id="name"
            type="text"
            onChange={onChange}
            value={values.name}
          />
          <label htmlFor="name">Trading Partner Name</label>
        </div>
        <div className="input-field col s12 m4">
          <select
            id="type"
            className="tpGenerealSelect"
            value={values.type}
            onChange={onChange}
          >
            <option value="upstream">Upstream (Buy From)</option>
            <option value="downstream">Downstream (Sell To)</option>
            <option value="both">Both (Buy/Sell)</option>
          </select>
          <label htmlFor="type">Trading Partner Type</label>
        </div>
      </div>
      <div className="row" style={{ marginBottom: '40px' }}>
        <GlobalLocationNumbers
          globalLocationNumbers={globalLocationNumbers}
          setGlobalLocatoinNumbers={setGlobalLocationNumbers}
        />
      </div>
      <div className="row" style={{ marginBottom: '40px' }}>
        <Addresses addresses={addresses} setAddresses={setAddresses} />
      </div>
      <div className="row" style={{ marginBottom: '40px' }}>
        <Contacts contacts={contacts} setContacts={setContacts} />
      </div>
      <AccountNumbers
        accountNumbers={accountNumbers}
        setAccountNumbers={setAccountNumbers}
      />
      <div className="row">
        <div className="input-field col s12 m4">
          <a
            href="/"
            className={
              customerLocationList.length > 0
                ? 'btn-small blue white-text waves-effect waves-light col s12'
                : 'btn-small grey white-text waves-effect waves-light col s12'
            }
            onClick={
              customerLocationList.length > 0
                ? createTradingPartner
                : event => event.preventDefault()
            }
          >
            Create Trading Partner
          </a>
        </div>
      </div>
    </div>
  );
};

export default General;
